import type { AvailableConnection, NodeConnectorInfo } from '@/types';
import { extractConnectorName, isAgentConnectionId, isServiceProviderConnector } from './connector';

/**
 * Connection-type bucket derived from a connector/API id, used by the
 * connector-picker surfaces to group and filter connections.
 */
export type MergedConnectionType = 'agent' | 'managedApi' | 'serviceProvider';

/**
 * A connection flattened for the connector-picker surfaces (the node
 * ConnectionTab and the template connections dialog). Both surfaces merge live
 * data-plane connections with locally-created store references into this shape
 * before rendering a `CompactConnectionItem` list.
 */
export interface MergedConnection {
  /** Reference key — the connection resource name / workflow store ref key. */
  key: string;
  /** Human-readable display name. */
  name: string;
  /** Connection-type bucket. */
  type: MergedConnectionType;
  /** Connector/API id the connection belongs to. */
  apiId: string;
  /** Underlying connection resource id, when known. */
  connectionId?: string;
  /** Connector icon URL, when resolvable. */
  iconUri?: string;
}

/**
 * Classify a connector/API id into the picker's three connection-type buckets.
 * Agent ids win over service-provider ids; everything else is a managed API.
 */
export const classifyMergedConnectionType = (apiId: string): MergedConnectionType => {
  if (isAgentConnectionId(apiId)) return 'agent';
  if (isServiceProviderConnector(apiId)) return 'serviceProvider';
  return 'managedApi';
};

/**
 * Map data-plane API connections into the flattened {@link MergedConnection}
 * shape used by the picker surfaces. The connector icon is resolved via
 * `getConnectorInfo`, tried by the connection name first and then by the
 * connector name extracted from the API id (mirroring how the surfaces resolve
 * icons for both friendly-aliased and raw connections).
 */
export const mapApiConnectionsToMerged = (
  apiConnections: AvailableConnection[],
  getConnectorInfo: (name: string) => NodeConnectorInfo | undefined,
): MergedConnection[] =>
  apiConnections.map((conn) => {
    const apiId = conn.connectorId ?? '';
    const apiName = extractConnectorName(apiId);
    const info = getConnectorInfo(conn.name) ?? getConnectorInfo(apiName);
    return {
      key: conn.name,
      name: conn.displayName ?? conn.name,
      type: classifyMergedConnectionType(apiId),
      apiId,
      ...(conn.id ? { connectionId: conn.id } : {}),
      ...(info?.iconUri ? { iconUri: info.iconUri } : {}),
    };
  });

/**
 * Case-insensitive substring filter over a connection list, matching either the
 * reference key or the display name. An empty query returns the list unchanged.
 */
export const filterConnectionsBySearch = <T extends { key: string; name: string }>(
  connections: T[],
  searchQuery: string,
): T[] => {
  if (!searchQuery) return connections;
  const query = searchQuery.toLowerCase();
  return connections.filter(
    (c) => c.key.toLowerCase().includes(query) || c.name.toLowerCase().includes(query),
  );
};
